import { TFunction } from 'i18next';
import { Operation } from '../../Transactions.types';
import { formatOperationAmount } from '../../Transactions.helpers';

export interface OperationDetailsRow {
  label: string;
  value: string;
  isTotal?: boolean;
}

function isNumericOperationNumber(operation: Operation): boolean {
  return /^\d+$/.test(operation.operationNumber);
}

function isNonEmpty(row: OperationDetailsRow): boolean {
  return row.value !== '';
}

function getLeadingRows(
  operation: Operation,
  t: TFunction
): OperationDetailsRow[] {
  return [
    { label: t('pages.cabinet.date'), value: operation.date },
    {
      label: t('pages.cabinet.payment_number_title'),
      value: isNumericOperationNumber(operation)
        ? `№ ${operation.operationNumber}`
        : '',
    },
  ];
}

export function getOperationTitle(operation: Operation, t: TFunction): string {
  if (operation.isRefund) {
    return t('pages.cabinet.refund_number', {
      number: operation.operationNumber,
    });
  }

  if (isNumericOperationNumber(operation)) {
    return t('pages.cabinet.payment_number', {
      number: operation.operationNumber,
    });
  }

  return t('pages.cabinet.payment');
}

export function getOperationRows(
  operation: Operation,
  t: TFunction
): OperationDetailsRow[] {
  const amount = formatOperationAmount(
    operation.amount,
    operation.direction,
    'AZN'
  );

  if (operation.isRefund) {
    return getRefundRows(operation, t, amount);
  }

  return [
    ...getLeadingRows(operation, t),
    {
      label: t('pages.cabinet.ad_number'),
      value: operation.ad?.legacyResourceId?.toString() ?? '',
    },
    { label: t('pages.cabinet.service'), value: operation.serviceName },
    {
      label: t('pages.cabinet.payment_type'),
      value: operation.paymentMethodLabel ?? '',
    },
    { label: t('common.sum'), value: amount },
    { label: t('pages.cabinet.total'), value: amount, isTotal: true },
  ].filter(isNonEmpty);
}

function getRefundRows(
  operation: Operation,
  t: TFunction,
  amount: string
): OperationDetailsRow[] {
  const { refund } = operation;

  return [
    ...getLeadingRows(operation, t),
    { label: t('pages.cabinet.ad_number'), value: refund?.adNumber ?? '' },
    {
      label: t('pages.cabinet.service'),
      value: refund?.paidServiceName ?? '',
    },
    {
      label: t('pages.cabinet.payment_type'),
      value: refund?.paidPaymentMethodLabel ?? '',
    },
    { label: t('pages.cabinet.operation'), value: operation.serviceName },
    { label: t('pages.cabinet.rrn_code'), value: refund?.rrn ?? '' },
    { label: t('pages.cabinet.refund_amount'), value: amount, isTotal: true },
  ].filter(isNonEmpty);
}
